<script>
import SettingsSidebar from "@/components/rubrics/newRubric/settings_sidebar.vue";
import RubricForm from "@/components/rubrics/rubric_form.vue";
import UploadContentArea from "@/components/dashboard/upload_content_area.vue";
import Settings from "@/components/rubrics/newRubric/settings.vue";
import {
  generateRubricByAiByUpload,
  getRubricGenerationJobStatus,
} from "@/api";
import { createPolling } from "@/utils/polling";
import RubricAiLoadingFullscreen from "@/components/rubrics/components/rubric_ai_loading_fullscreen.vue";
import RubricFullscreenModal from "@/components/rubrics/components/rubric_fullscreen_modal.vue";
import RubricFormActions from "@/components/rubrics/newRubric/components/rubric_form_actions.vue";
import RubricSettingsTab from "@/components/rubrics/components/rubric_settings_tab.vue";
import RubricPreviewTab from "@/components/rubrics/components/rubric_preview_tab.vue";
import rubricFormStateMixin from "@/mixins/rubricFormStateMixin";
import onboardingVueEventMixin from "@/mixins/onboardingVueEventMixin";

export default {
  name: "RubricAiUpload",

  components: {
    Settings,
    RubricForm,
    SettingsSidebar,
    UploadContentArea,
    RubricAiLoadingFullscreen,
    RubricFullscreenModal,
    RubricFormActions,
    RubricSettingsTab,
    RubricPreviewTab,
  },
  mixins: [
    rubricFormStateMixin,
    onboardingVueEventMixin("onboarding:mounted:createRubricFromFile"),
  ],
  inject: ["clearRubricSettings", "newRubricSettings"],
  emits: ["createRubric", "close"],

  data() {
    return {
      step: 0,
      settings: {
        grade: true,
        intensity: true,
        language: true,
      },
      rubric: null,
      filesToUpload: [],
      file: null,
      aiLoading: false,
      uploadInProgress: false,
      selectedType: "files",
      // Polling state
      polling: null,
      // Additional criteria toggles
      // includeMLA: false,
      // includeAPA: false,
      uploadAreaKey: 0,
    };
  },
  beforeMount() {
    this.clearRubricSettings();
    this.initPolling();
  },
  beforeDestroy() {
    if (this.polling) {
      this.polling.stop();
    }
  },

  mounted() {
    this.emitOnboardingEvent();
  },

  methods: {
    initPolling() {
      this.polling = createPolling({
        callback: this.pollRubricStatus,
        interval: 3000,
        onError: this.handlePollingError,
      });
    },

    async pollRubricStatus() {
      const { data } = await getRubricGenerationJobStatus(
        this.polling.currentJobId
      );

      if (data.status === "ready") {
        this.handlePollingSuccess(data);
      } else if (data.status === "failed") {
        throw new Error("Rubric generation failed");
      }

      return data;
    },

    handlePollingSuccess(rubricData) {
      this.rubric = rubricData;
      this.updateSettingsFromRubric(rubricData);
      this.aiLoading = false;

      if (this.polling?.isPolling) {
        this.polling.stop();
      }

      // The rubricBuilder onboarding fires on mount but the form isn't rendered
      // yet (rubric is null). Re-fire after the rubric is set so elements exist.
      this.emitRubricBuilderOpened();
    },

    handlePollingError(error) {
      console.error("Polling error:", error);
      this.$showToastError(
        error.response?.data?.message ||
          "There was a problem creating the rubric. Please try again, or contact support",
        { title: "Rubric Creation failed" }
      );
      this.aiLoading = false;
      this.step = 0;
    },

    /**
     * Start the AI rubric generation process
     */
    async callAi() {
      // Ensure we're on step 1 with loading state
      if (this.step !== 1) {
        this.step = 1;
        this.aiLoading = true;
      }

      try {
        const { data } = await generateRubricByAiByUpload({
          category:
            this.newRubricData.categories[
              this.newRubricSettings.selectedCategoryIdx
            ].value,
          gradeLevel: this.newRubricSettings.selectedGradeLevel,
          intensity:
            this.newRubricData.intensities[
              this.newRubricSettings.selectedIntensityIdx
            ].value,
          LanguageId: this.newRubricSettings.selectedLanguageId,
          fileUrls: [this.file.fileUrl],
          // includeMLA: this.includeMLA,
          // includeAPA: this.includeAPA,
        });

        // Store job ID and start polling
        if (data.jobId) {
          this.polling.currentJobId = data.jobId;
          this.polling.start();
        } else if (data.status === "ready") {
          this.handlePollingSuccess(data);
        } else {
          throw new Error("Invalid response from server");
        }
      } catch (e) {
        console.error("Error creating rubric:", e);
        this.$showToastError(
          "There was a problem creating the rubric. Please try again, or contact support",
          { title: "Rubric Creation failed" }
        );
        this.step = 0;
        this.aiLoading = false;
      }
    },

    onUpload(e) {
      this.file = e[0];
      this.callAi();
    },
    onFileAdded(e) {
      // File selected but not yet uploaded - show it immediately
      this.filesToUpload = e;
      if (e.length > 0) {
        this.file = e[0]; // Show file immediately in custom UI
      }
    },
    onFileRemoved(e) {
      this.filesToUpload = e;
      if (e.length === 0) {
        this.file = null; // Clear file from custom UI
      }
    },
    async upload() {
      // If file is already uploaded (has fileUrl), just call AI again with new settings
      if (this.file?.fileUrl) {
        this.callAi();
        return;
      }

      // Move to step 1 immediately to show loading screen
      this.step = 1;
      this.aiLoading = true;

      // Upload the file in background
      try {
        this.uploadInProgress = true;
        await this.$refs.uploadArea.upload();
        this.uploadInProgress = false;
        // After upload completes, onUpload will be called which will call callAi()
      } catch (e) {
        console.error("Error uploading file:", e);
        this.$showToastError(
          "There was a problem uploading the file. Please try again, or contact support",
          { title: "File Upload failed" }
        );
        this.step = 0;
        this.aiLoading = false;
        this.uploadInProgress = false;
      }
    },
    onSaveSettings() {
      if (this.step === 1) {
        this.callAi();
      }
    },
    goBackToUpload() {
      if (this.polling?.isPolling) {
        this.polling.stop();
      }

      this.step = 0;
      this.aiLoading = false;
    },
    clearFile() {
      this.file = null;
      this.filesToUpload = [];
      this.rubric = null;
      this.uploadAreaKey++; // Force re-render of upload area with fresh state
    },
  },
};
</script>

<template>
  <div class="rubric-ai-upload-wrapper">
    <!-- Fullscreen Loading Overlay -->
    <RubricAiLoadingFullscreen
      v-show="step === 1 && aiLoading"
      @back="goBackToUpload"
    />

    <!-- Fullscreen Rubric Form -->
    <RubricFullscreenModal
      v-show="step === 1 && !aiLoading"
      ref="fullscreenModal"
      mode="create"
      :show-tabs="true"
      @cancel="$emit('close')"
      @save="$refs.rubricForm.handleSubmit()"
      @back="goBackToUpload"
    >
      <!-- Edit Tab Content -->
      <template #default>
        <rubric-form
          v-if="rubric"
          :key="`ai-upload-form-${rubric?.id || 'new'}`"
          ref="rubricForm"
          :rubric="rubric"
          :editing="true"
          :hide-actions="true"
          :initial-data="rubricFormData"
          @rubricSubmitted="$emit('createRubric', $event)"
          @formDataUpdate="handleFormDataUpdate"
          @switchToFirstTab="$refs.fullscreenModal.activeTab = 0"
          @close="$emit('close')"
        />
      </template>

      <!-- Preview Tab Content -->
      <template #preview>
        <RubricPreviewTab
          :key="`preview-${rubricFormData.name}-${
            rubricFormData.criterias?.length || 0
          }`"
          :rubric="rubricFormData"
        />
      </template>

      <!-- Settings Tab Content -->
      <template #settings>
        <RubricSettingsTab grade intensity language />
      </template>
    </RubricFullscreenModal>

    <!-- Normal Modal Content -->
    <div v-show="step === 0">
      <eg-b-row class="full-width rubric-form-body" no-gutters>
        <SettingsSidebar v-bind="settings" @onSave="onSaveSettings" />
        <eg-b-col cols="12" class="rubric-form-content">
          <!-- Upload or Import Section -->
          <div
            class="mb-4"
            data-testid="create-rubric-from-file-upload-or-import"
          >
            <h3 class="paragraph-m">
              {{ $t("common.uploadOrImport") }}
              <eg-button
                type="ordinary"
                icon="information"
                color="var(--color-icon-neutral-neutral)"
                icon-size="small"
                size="extra-small"
                @click="
                  $eventBus.$emit('onboarding:clicked:createRubricFromFile')
                "
              />
            </h3>
            <!-- Show already uploaded file if exists -->
            <div v-if="file" class="uploaded-file-card-container mb-3">
              <div class="uploaded-file-card">
                <div class="file-icon">
                  <eg-b-icon icon="file-earmark-text" font-scale="1.5" />
                </div>
                <div class="file-name">
                  {{ file.name || file.fileName || "Rubric file" }}
                </div>
                <eg-button
                  type="ordinary"
                  size="medium"
                  icon="close"
                  @click="clearFile"
                />
              </div>
            </div>

            <!-- Show upload area only if no file selected -->
            <UploadContentArea
              v-show="!file"
              :key="`upload-${uploadAreaKey}`"
              ref="uploadArea"
              v-model="selectedType"
              class="upload-content-area mt-3"
              label="Upload rubric"
              required
              only-files
              :path="'ai-rubrics'"
              :max-number-of-files="1"
              :available-variants="['files']"
              @input-files="onUpload($event)"
              @file-added="onFileAdded($event)"
              @file-removed="onFileRemoved($event)"
            />
          </div>
          <Settings grade intensity language />

          <!--&lt;!&ndash; Additional Criteria Section &ndash;&gt;
          <div class="mb-4">
            <h3>Additional Criteria</h3>
            <div class="d-flex flex-column" style="gap: 1rem">
              <eg-b-form-checkbox v-model="includeMLA" switch>
                MLA (Modern Language Association style)
              </eg-b-form-checkbox>
              <eg-b-form-checkbox v-model="includeAPA" switch>
                APA (American Psychological Association style)
              </eg-b-form-checkbox>
            </div>
          </div>-->
        </eg-b-col>
      </eg-b-row>
      <!-- Action Buttons -->
      <RubricFormActions
        :on-cancel="() => $emit('close')"
        :on-submit="upload"
        :submit-disabled="
          selectedType === 'files' &&
          (filesToUpload.length === 0 || uploadInProgress)
        "
        :submit-loading="uploadInProgress"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.rubric-ai-upload-wrapper {
  position: relative;
  width: 100%;
}

.file {
  border-bottom: solid #dfdfdf 1px;
  display: inline-flex;
  align-items: center;
  padding: 0.4rem 1rem;
  span {
    margin-right: 2rem;
    margin-left: 0.5rem;
  }
}

.uploaded-file-card-container,
.uploaded-file-card {
  border-radius: var(--radius-rd-300, 8px);
  border: 1px solid var(--color-stroke-neutral-default, #e5ebf0);
}
.uploaded-file-card-container {
  padding: 1.5rem;
  border-radius: var(--radius-rd-400, 12px);
}
.uploaded-file-card {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  gap: 12px;

  .file-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #6b7280;
  }

  .file-name {
    flex: 1;
    font-size: 14px;
    font-weight: 500;
    color: #1f2937;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.step-hidden {
  display: none;
}
</style>
